import React from 'react';

const MusicQuizGame = ({
  questionNumber = 1,
  totalQuestions = 10,
  score = 0,
  questionText = "",
  coverImage = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
  answerChoices = [
    "Don't Start Now",
    "Blinding Lights",
    "Levitating",
    "Watermelon Sugar"
  ],
  teamMembers = [],
  onAnswer = () => {},
  onSubmit = () => {},
  onPlayVideo = () => {},
  onMenuClick = () => {},
  selectedAnswer = null,
  isLoading = false,
  isTeamAdmin = false,
  teamAnswers = {},
  currentUserId = null,
  allMembersAnswered = false,
  hasAnsweredCurrentQuestion = false,
  gameStatus = "active",
  userPreviousAnswer = null
}) => {
  const progressPercentage = (questionNumber / totalQuestions) * 100;

  // Show different UI based on game status
  if (gameStatus === "waiting") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Game Starting Soon</h2>
          <p className="text-white/70 text-lg">Waiting for host to start the game...</p>
          <button
            onClick={onMenuClick}
            className="mt-6 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  if (gameStatus === "completed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
            <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Game Completed!</h2>
          <p className="text-white/70 text-lg mb-2">Final Score: {score}</p>
          <p className="text-white/70">Check the leaderboard for final standings</p>
          <button
            onClick={onMenuClick}
            className="mt-6 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  // If user has answered current question, show waiting state
  if (hasAnsweredCurrentQuestion && userPreviousAnswer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 text-white flex flex-col p-4">
        <div className="mx-auto w-full flex-1 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-pink-500 to-purple-600 opacity-20 blur-xl"></div>

          <div className="relative z-10 h-full flex flex-col justify-center items-center p-4">
            {/* Header */}
            <div className="flex items-center mb-6 bg-purple-900/50 rounded-full px-4 py-3 w-full">
              <button
                onClick={onMenuClick}
                aria-label="Go back"
                className="w-8 h-8 flex justify-center hover:scale-110 transition-transform"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-xl font-bold tracking-wider">MUSIC QUIZ</h1>
              <div className="w-8 h-8"></div>
            </div>

            {/* Waiting Message */}
            <div className="text-center mb-8">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center animate-pulse">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Waiting for Next Question</h2>
              <p className="text-cyan-300 text-lg mb-1">Question {questionNumber} of {totalQuestions}</p>
              <p className="text-white/60">Waiting for host to advance...</p>
            </div>

            {/* Your Answer Review */}
            <div className="bg-purple-900/50 rounded-xl p-6 backdrop-blur-sm border-2 border-cyan-400/30 w-full max-w-md">
              <h3 className="text-cyan-300 font-bold text-lg mb-4 text-center">Your Answer:</h3>
              <div className="bg-purple-800/50 rounded-lg p-4 border-2 border-green-400/50">
                <p className="text-white text-center text-lg font-semibold">{userPreviousAnswer.answerText}</p>
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 text-green-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm">Submitted for review</span>
              </div>
            </div>

            {/* Score Display */}
            <div className="mt-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full px-6 py-3 shadow-lg">
              <span className="text-sm font-medium">CURRENT SCORE: </span>
              <span className="text-2xl font-bold">{score}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 text-white flex flex-col rounded-2xl">
      {/* Phone Frame Container */}
      <div className="mx-auto w-full flex-1 relative overflow-hidden">
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-pink-500 to-purple-600 opacity-20 blur-xl"></div>
        
        {/* Main Content */}
        <div className="relative z-10 h-full flex flex-col p-4">
          
          {/* Header Section */}
          <div className="flex items-center mb-6 bg-purple-900/50 rounded-full px-4 py-3">
            <button 
              onClick={onMenuClick}
              aria-label="Go back"
              className="w-8 h-8 flex justify-center hover:scale-110 transition-transform"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h1 className="flex-1 text-center text-xl font-bold tracking-wider mb-0 mt-1">MUSIC QUIZ</h1>
            
            {/* Empty div for spacing to keep title centered */}
            <div className="w-8 h-8"></div>
          </div>

          {/* Question Progress Section */}
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl font-black text-cyan-300">QUESTION {questionNumber}/{totalQuestions}</span>
              </div>
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-full px-4 py-2 shadow-lg shadow-purple-500/50">
                <span className="text-sm font-medium">SCORE</span>
                <div className="text-2xl font-bold">{score}</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-2 bg-purple-900/50 rounded-full overflow-hidden backdrop-blur-sm">
              <div 
                className="h-full bg-gradient-to-r from-pink-500 to-cyan-400 rounded-full transition-all duration-500 shadow-lg"
                style={{ width: `${progressPercentage}%` }}
                role="progressbar"
                aria-valuenow={questionNumber}
                aria-valuemin="1"
                aria-valuemax={totalQuestions}
              ></div>
            </div>
          </div>

          {/* Question Text */}
          {questionText && (
            <div className="mb-4 p-4 bg-purple-900/50 rounded-xl backdrop-blur-sm">
              <h2 className="text-xl font-bold text-center text-cyan-300">{questionText}</h2>
            </div>
          )}

          {/* Quiz Image Section */}
          <div className="relative mb-6 rounded-2xl overflow-hidden border-2 border-transparent bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-600 p-0.5">
            <div className="relative bg-purple-900 rounded-2xl overflow-hidden">
              <img 
                src={coverImage} 
                alt="Quiz question visual"
                className="w-full h-48 object-cover"
              />
              {/* Play Button Overlay */}
              <button 
                onClick={onPlayVideo}
                aria-label="Play video"
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
              >
                <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                  <svg className="w-8 h-8 text-purple-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </button>
            </div>
          </div>

          {/* Answer Buttons */}
          <div className="space-y-3 mb-6 flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-cyan-300">Loading questions...</div>
              </div>
            ) : (
              answerChoices.map((answer, index) => {
                const answerLabel = ['A', 'B', 'C', 'D'][index];
                return (
                  <div
                    key={index}
                    className={`relative rounded-full p-1.5 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                      selectedAnswer === index 
                        ? 'bg-gradient-to-r from-green-400 via-cyan-400 to-blue-400' 
                        : 'bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-400 hover:from-pink-400 hover:via-purple-400 hover:to-cyan-300'
                    }`}
                  >
                    <button
                      onClick={() => onAnswer(answer)}
                      aria-label={`Select answer ${answerLabel}: ${answer}`}
                      className={`w-full py-4 px-6 rounded-full backdrop-blur-sm transition-colors duration-300 flex items-center gap-4 ${
                        selectedAnswer === index 
                          ? 'bg-green-900/95 hover:bg-green-800/95' 
                          : 'bg-purple-950/95 hover:bg-purple-950/80'
                      }`}
                    >
                      <span className={`text-2xl font-bold w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedAnswer === index 
                          ? 'bg-green-600/50' 
                          : 'bg-purple-800/50'
                      }`}>
                        {answerLabel}
                      </span>
                      <span className="text-2xl font-semibold flex-1 text-left">{answer}</span>
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Team Members Section */}
          <div className="mt-auto pt-4 border-t border-purple-700/50">
            <div className="bg-gradient-to-r from-cyan-400/10 to-pink-500/10 rounded-full px-4 py-3 backdrop-blur-sm border border-cyan-400/30">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-cyan-300">Team</span>
                <div className="flex -space-x-2 overflow-x-auto flex-1">
                  {teamMembers && teamMembers.length > 0 ? (
                    teamMembers.map((member) => {
                      const memberAnswer = teamAnswers[member.id];
                      const answerLabel = memberAnswer !== undefined ? ['A', 'B', 'C', 'D'][memberAnswer.answer] : null;
                      
                      return (
                        <div 
                          key={member.id}
                          className="relative group"
                          title={`${member.displayName}${answerLabel ? ` - Answer: ${answerLabel}` : ''}`}
                        >
                          {member.photoURL ? (
                            <img
                              src={member.photoURL}
                              alt={member.displayName}
                              className="w-12 h-12 rounded-full border-2 border-purple-900 hover:z-10 hover:scale-110 transition-transform cursor-pointer object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full border-2 border-purple-900 hover:z-10 hover:scale-110 transition-transform cursor-pointer bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-semibold text-sm">
                              {(member.displayName || member.email || "?")[0].toUpperCase()}
                            </div>
                          )}
                          {/* Crown icon for team admin */}
                          {member.role === "admin" && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center text-xs z-20">
                              👑
                            </div>
                          )}
                          {/* Answer indicator */}
                          {answerLabel && (
                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full flex items-center justify-center text-xs font-bold text-white z-20 shadow-lg">
                              {answerLabel}
                            </div>
                          )}
                          {/* Gradient border effect on hover */}
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-sm"></div>
                        </div>
                      );
                    })
                  ) : (
                    <span className="text-xs text-gray-400">No team members yet</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Submit Answer Button */}
            <div className="mt-3">
              {isTeamAdmin ? (
                <div className={`relative rounded-full p-1.5 transition-all duration-300 transform shadow-lg ${
                  selectedAnswer !== null && allMembersAnswered
                    ? 'bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 hover:from-yellow-300 hover:via-orange-400 hover:to-pink-400 hover:scale-[1.02] active:scale-[0.98] shadow-orange-500/50' 
                    : 'bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 cursor-not-allowed opacity-50'
                }`}>
                  <button
                    onClick={onSubmit}
                    disabled={selectedAnswer === null || !allMembersAnswered}
                    aria-label="Submit your answer"
                    className={`w-full py-4 px-6 rounded-full backdrop-blur-sm transition-all duration-300 ${
                      selectedAnswer !== null && allMembersAnswered
                        ? 'bg-gradient-to-r from-orange-600/95 to-pink-600/95 hover:from-orange-500/95 hover:to-pink-500/95 cursor-pointer' 
                        : 'bg-gray-700/95 cursor-not-allowed'
                    }`}
                  >
                    <span className="text-2xl font-bold text-white drop-shadow-lg">
                      {selectedAnswer === null 
                        ? 'Select an Answer' 
                        : !allMembersAnswered 
                          ? `Waiting for team (${Object.keys(teamAnswers || {}).length}/${teamMembers.length})`
                          : 'Submit Answer'}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <span className="text-lg text-cyan-300">
                    Only team admin can submit
                  </span>
                  {selectedAnswer !== null && (
                    <div className="mt-2 text-sm text-gray-400">
                      Your answer: {['A', 'B', 'C', 'D'][selectedAnswer]}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicQuizGame;